import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getProductsSortedByAddedAtDesc } from "./utils/productSorter";
import { auth, db } from "./firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  getDocs,
  getDoc
} from "firebase/firestore";
// Firebase Functions will be imported dynamically when needed
import { loadStripe } from "@stripe/stripe-js";
import Navbar from "./Navbar";
import CollaboratorsModal from "./CollaboratorsModal";
import SwipeWrapper from './SwipeWrapper';
import PremiumModal from './PremiumModal';


export default function Archived() {
  const { t } = useTranslation();
  const [wishlists, setWishlists] = useState([]);
  const [expandedLists, setExpandedLists] = useState({});
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const [activeWishlistCount, setActiveWishlistCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/feed";

  // Funzione per gestire il checkout Stripe
  const handleUpgradeToPremium = async () => {
    if (!auth.currentUser) {
      console.error("Utente non autenticato");
      return;
    }

    try {
      console.log("🔄 Inizializzazione checkout Stripe...");

      // Inizializza le Firebase Functions
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      // Chiama la Cloud Function per creare la sessione
      const result = await createCheckoutSession({
        userId: auth.currentUser.uid
      });

      console.log("✅ Sessione Stripe creata:", result.data);

      // Inizializza Stripe
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
      if (!stripe) {
        throw new Error("Errore nell'inizializzazione di Stripe");
      }

      // Reindirizza al checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId: result.data.sessionId
      });

      if (error) {
        console.error("❌ Errore nel reindirizzamento:", error);
        alert(t('auth.errors.paymentRedirectError'));
      }

    } catch (error) {
      console.error("❌ Errore nell'upgrade a Premium:", error);
      alert(t('auth.errors.paymentSessionError'));
    }
  };


  useEffect(() => {
     const checkPremiumStatus = async () => {
        try {
          const userRef = doc(db, "users", auth.currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            setIsPremium(data.isPremium || false);
          }
        } catch (error) {
          console.error("Errore nel recupero dello stato premium:", error);
        }
      };

      checkPremiumStatus();

    const fetchActiveWishlists = async () => {
      const q = query(
        collection(db, "wishlists"), 
        where("userId", "==", auth.currentUser.uid), 
        where("archived", "!=", true)
      );
      const snap = await getDocs(q);
      setActiveWishlistCount(snap.docs.length);
    };
    fetchActiveWishlists();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setIsPremium(userSnap.data().isPremium || false);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "wishlists"),
      where("userId", "==", auth.currentUser.uid),
      where("archived", "==", true)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const newWishlists = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      
      setWishlists(newWishlists);
      
      const savedScrollPosition = sessionStorage.getItem('fromScroll');
      const fromPage = sessionStorage.getItem('fromPage');
      const fromBottomNav = location.state?.fromBottomNav;
      const shouldRestore = !fromBottomNav && savedScrollPosition && fromPage === '/archivio';
      
      if (shouldRestore && newWishlists.length > 0) {
        setTimeout(() => {
          const position = parseInt(savedScrollPosition, 10);
          if (!isNaN(position)) {
            window.scrollTo({
              top: position,
              behavior: 'smooth'
            });
            
            sessionStorage.removeItem('fromScroll');
            sessionStorage.removeItem('fromPage');
          }
        }, 800);
      }
    });

    return () => unsubscribe();
  }, []);

  const restoreWishlist = async (id) => {
    if (activeWishlistCount >= 10 && !isPremium) {
      setShowLimitModal(true);
      return;
    }

    await updateDoc(doc(db, "wishlists", id), {
      archived: false
    });
  };

  const toggleExpand = (id) => {
    const wishlistElement = document.querySelector(`[data-wishlist-id="${id}"]`);
    const currentScrollY = window.scrollY;
    const elementTop = wishlistElement ? wishlistElement.offsetTop : 0;

    setExpandedLists(prev => {
      const wasExpanded = prev[id];

      // If we're collapsing, scroll back to the wishlist header
      if (wasExpanded) {
        setTimeout(() => {
          const headerOffset = 80; // Account for navbar
          window.scrollTo({
            top: elementTop - headerOffset,
            behavior: 'smooth'
          });
        }, 50);
      }

      return { ...prev, [id]: !prev[id] };
    });
  };

  return (
    <SwipeWrapper redirectTo={from}>
      <div style={styles.page}>
        <Navbar />
      <div style={styles.container}>
        <h2 style={styles.title}>{t('navigation.archived')}</h2>
        {wishlists.length === 0 && (
          <div style={{
            textAlign: "center",
            padding: "40px 20px",
            color: "#666",
            fontSize: "16px"
          }}>
            {t('home.noArchivedWishlists')}
          </div>
        )}

        {wishlists.map((w) => {
          const expanded = expandedLists[w.id];
          // Products are now stored newest-first in database
          const sortedProducts = w.products || [];
          const visibleProducts = expanded ? sortedProducts : sortedProducts.slice(0, 3);

          return (
            <div key={w.id} data-wishlist-id={w.id} style={styles.card}>
              {w.eventName && (
                <div style={{
                  fontSize: "12px",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  color: "#d00",
                  backgroundColor: "#ffe5e5",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  display: "inline-block"
                }}>
                  {w.eventName}
                </div>
              )}
              <div style={styles.header}>
                <h3 style={styles.name}>{w.name}</h3>
                <button onClick={() => restoreWishlist(w.id)} style={styles.restoreBtn}>
                  {t('wishlist.unarchive')}
                </button>
              </div>
              {(w.tags || []).length > 0 && (
                <div style={styles.tags}>
                  {w.tags.map((tag, i) => (
                    <span key={i} style={styles.tag}>#{tag}</span>
                  ))}
                </div>
              )}
              <div style={styles.products}>
                {visibleProducts.map((p, i) => (
                  <div key={i} style={styles.product}>
                    <img src={p.image} alt="" style={styles.image} />
                    <a href={p.url} target="_blank" rel="noreferrer" style={styles.link}>
                      {p.title}
                    </a>
                  </div>
                ))}
                {(w.products?.length || 0) > 3 && (
                  <button onClick={() => toggleExpand(w.id)} style={styles.showMore}>
                    {expanded ? t('wishlist.showLess') : `+ ${w.products.length - 3} ${t('wishlist.moreProducts')}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <PremiumModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onUpgrade={handleUpgradeToPremium}
      />

      {showCollaboratorsModal && (
        <CollaboratorsModal
          wishlistId={showCollaboratorsModal}
          currentUserId={auth.currentUser?.uid}
          onClose={() => setShowCollaboratorsModal(null)}
          viewOnly={false}
        />
      )}
      </div>
    </SwipeWrapper>
  );
}

const styles = {
  page: {
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
    color: "#000",
    minHeight: "100vh",
    paddingBottom: "80px"
  },
  container: {
    padding: "20px"
  },
  title: {
    fontSize: "22px",
    fontWeight: "600",
    marginBottom: "20px"
  },
  card: {
    background: "#f9f9f9",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "20px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px"
  },
  name: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#000",
    margin: 0,
    flex: 1
  },
  restoreBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer"
  },
  tags: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    margin: "10px 0"
  },
  tag: {
    fontSize: "12px",
    background: "#eee",
    padding: "4px 8px",
    borderRadius: "20px"
  },
  products: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  product: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "8px"
  },
  image: {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "6px"
  },
  link: {
    color: "#000",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "500",
    flex: 1
  },
  showMore: {
    background: "none",
    border: "none",
    color: "#000",
    fontSize: "13px",
    cursor: "pointer",
    marginTop: "5px",
    textAlign: "left",
    padding: 0
  }
};